"use client";

import { Button, HStack, IconButton, Text } from "@chakra-ui/react";
import { useUserChoices } from "@/state/globalStore";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import WithLongPress from "@/shared/ui/gestures/WithLongPress";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import usePageSearch from "../hooks/usePageSearch";

const OPEN_SEARCH_FILTERS_LABEL = "Open search filters";
const RESET_SEARCH_FILTERS_HINT = "Press long to reset filters";

type OpenSearchFiltersButtonProps = {
  disabled?: boolean;
  showLabel?: boolean;
};

export default function OpenSearchFiltersButton({
  disabled = false,
  showLabel = true,
}: OpenSearchFiltersButtonProps) {
  const { hasNonDefaultSearchAdjustments, searchControls } = usePageSearch();
  const { setInterimState } = useUserChoices();
  const isResetEnabled = hasNonDefaultSearchAdjustments && !disabled;

  return (
    <WithLongPress
      onLongPress={searchControls.resetSearchAdjustments}
      enabled={isResetEnabled}
      preventClickAfterLongPress={true}
      longPressHint={isResetEnabled ? RESET_SEARCH_FILTERS_HINT : undefined}
    >
      {showLabel ? (
        <Button
          variant="ghost"
          onClick={() => {
            setInterimState("search_filters", { isOpen: true });
          }}
          title={OPEN_SEARCH_FILTERS_LABEL}
          aria-label={OPEN_SEARCH_FILTERS_LABEL}
          borderRadius="lg"
          minW="auto"
          h="auto"
          px="2.5"
          py="2"
          disabled={disabled}
        >
          <HStack gap="2">
            <GenericIcon
              icon="adjust"
              size={iconSizePresets.actionButton}
              highlighted={hasNonDefaultSearchAdjustments}
              visuallyInactive={disabled}
              humanReadable={OPEN_SEARCH_FILTERS_LABEL}
            />
            <Text
              display={{ base: "none", md: "inline" }}
              fontSize="sm"
              fontWeight="medium"
              color={disabled ? "moveon.text.muted" : undefined}
            >
              Filters
            </Text>
          </HStack>
        </Button>
      ) : (
        <IconButton
          variant="ghost"
          size="xs"
          onClick={() => {
            setInterimState("search_filters", { isOpen: true });
          }}
          title={OPEN_SEARCH_FILTERS_LABEL}
          aria-label={OPEN_SEARCH_FILTERS_LABEL}
          colorPalette="gray"
          disabled={disabled}
        >
          <GenericIcon
            icon="adjust"
            size="sm"
            highlighted={hasNonDefaultSearchAdjustments}
            visuallyInactive={disabled}
            humanReadable={OPEN_SEARCH_FILTERS_LABEL}
          />
        </IconButton>
      )}
    </WithLongPress>
  );
}
